import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Sheet,
  Switch,
  Typography,
} from '@mui/joy';
import { Plus, Trash } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DetailDrawer,
  DetailRow,
  DetailSection,
} from '../../../components/common/DetailDrawer';
import { AdminSequencesAPI } from '../../../lib/apiAdmin';
import { getApiErrorMessage } from '../../../lib/apiError';
import type { OnboardingSequence, PlatformEmailTemplate } from '../../../types/admin';

/**
 * Conditions checked by the sequence runner before sending a step.
 */
const SKIP_SIGNALS: { value: string; label: string }[] = [
  { value: '', label: 'Always send' },
  { value: 'logged_in_recently', label: 'Skip if they logged in recently' },
  { value: 'did_core_work', label: 'Skip if they created a job or estimate' },
  { value: 'created_opportunity', label: 'Skip if they create an opportunity' },
  { value: 'used_crm', label: 'Skip if they added contacts' },
  { value: 'completed_setup', label: 'Skip if setup is done' },
  { value: 'invited_team', label: 'Skip if they invited their team' },
];

const ENROLMENT_COLORS: Record<string, 'primary' | 'success' | 'neutral' | 'warning'> = {
  active: 'primary',
  completed: 'success',
  cancelled: 'neutral',
  suppressed: 'warning',
};

/**
 * One sequence: when it sends, what it sends, and who is on it.
 *
 * Steps are keyed off days since signup rather than days since enrolment, so a
 * customer enrolled in week three starts at the step that matches where they
 * already are instead of receiving the day-one welcome.
 */
export const SequenceDetailDrawer: React.FC<{
  sequenceId: string | null;
  templates: PlatformEmailTemplate[];
  onClose: () => void;
}> = ({ sequenceId, templates, onClose }) => {
  const queryClient = useQueryClient();
  const open = sequenceId !== null;

  const [newStep, setNewStep] = useState<{ template: string; day_offset: string; skip: string }>({
    template: '',
    day_offset: '',
    skip: '',
  });

  const sequenceQuery = useQuery({
    queryKey: ['admin', 'sequence', sequenceId],
    queryFn: () => AdminSequencesAPI.getSequence(sequenceId as string),
    enabled: open,
  });

  const enrollmentsQuery = useQuery({
    queryKey: ['admin', 'enrollments', { sequence: sequenceId }],
    queryFn: () => AdminSequencesAPI.listEnrollments({ sequence: sequenceId as string, page_size: 50 }),
    enabled: open,
  });

  const sequence = sequenceQuery.data;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'sequence', sequenceId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'sequences'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'enrollments'] });
  };

  const updateMutation = useMutation({
    mutationFn: (changes: Partial<OnboardingSequence>) =>
      AdminSequencesAPI.updateSequence(sequenceId as string, changes),
    onSuccess: refresh,
  });

  const addStepMutation = useMutation({
    mutationFn: () =>
      AdminSequencesAPI.createStep({
        sequence: sequenceId as string,
        template: newStep.template,
        day_offset: Number(newStep.day_offset || 0),
        skip_if_signal: newStep.skip,
        // Steps are ordered by day, so the next order number is simply the
        // count so far; the API enforces uniqueness per sequence.
        order: (sequence?.steps.length ?? 0) + 1,
      }),
    onSuccess: () => {
      setNewStep({ template: '', day_offset: '', skip: '' });
      refresh();
    },
  });

  const deleteStepMutation = useMutation({
    mutationFn: (id: string) => AdminSequencesAPI.deleteStep(id),
    onSuccess: refresh,
  });

  const cancelEnrollment = useMutation({
    mutationFn: (id: string) => AdminSequencesAPI.cancelEnrollment(id),
    onSuccess: refresh,
  });

  const steps = [...(sequence?.steps ?? [])].sort((a, b) => a.day_offset - b.day_offset);
  const enrollments = enrollmentsQuery.data?.results ?? [];

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={sequence?.name ?? 'Sequence'}
      subtitle={
        sequence
          ? `${steps.length} step${steps.length === 1 ? '' : 's'} · ${sequence.enrolled_count} enrolled`
          : undefined
      }
      isLoading={sequenceQuery.isLoading}
      error={sequenceQuery.error as Error | null}
      width={640}
      actions={
        sequence ? (
          <Chip size="sm" variant="soft" color={sequence.is_active ? 'success' : 'neutral'}>
            {sequence.is_active ? 'Sending' : 'Paused'}
          </Chip>
        ) : undefined
      }
    >
      {sequence && (
        <>
          {updateMutation.isError && (
            <Alert color="danger" variant="soft">
              {getApiErrorMessage(updateMutation.error, 'That change did not save.')}
            </Alert>
          )}

          <DetailSection title="Settings">
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', display: 'grid', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box>
                  <Typography level="body-sm">Live</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    Paused sequences keep their enrollments but send nothing.
                  </Typography>
                </Box>
                <Switch
                  checked={sequence.is_active}
                  onChange={(event) => updateMutation.mutate({ is_active: event.target.checked })}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box>
                  <Typography level="body-sm">Enrol new signups automatically</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    Every organization that registers goes on this sequence.
                  </Typography>
                </Box>
                <Switch
                  checked={sequence.auto_enrol}
                  onChange={(event) => updateMutation.mutate({ auto_enrol: event.target.checked })}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <FormControl sx={{ flex: '1 1 160px' }}>
                  <FormLabel>From name</FormLabel>
                  <Input
                    size="sm"
                    defaultValue={sequence.from_name}
                    onBlur={(event) =>
                      event.target.value !== sequence.from_name &&
                      updateMutation.mutate({ from_name: event.target.value })
                    }
                  />
                </FormControl>
                <FormControl sx={{ flex: '1 1 160px' }}>
                  <FormLabel>Reply-to</FormLabel>
                  <Input
                    size="sm"
                    defaultValue={sequence.reply_to}
                    onBlur={(event) =>
                      event.target.value !== sequence.reply_to &&
                      updateMutation.mutate({ reply_to: event.target.value })
                    }
                  />
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <FormControl sx={{ width: { xs: '100%', sm: 110 } }}>
                  <FormLabel>Send after</FormLabel>
                  <Input
                    size="sm"
                    type="number"
                    slotProps={{ input: { min: 0, max: 23 } }}
                    defaultValue={sequence.send_after_hour}
                    onBlur={(event) =>
                      updateMutation.mutate({ send_after_hour: Number(event.target.value) })
                    }
                  />
                </FormControl>
                <FormControl sx={{ width: { xs: '100%', sm: 110 } }}>
                  <FormLabel>Send before</FormLabel>
                  <Input
                    size="sm"
                    type="number"
                    slotProps={{ input: { min: 1, max: 24 } }}
                    defaultValue={sequence.send_before_hour}
                    onBlur={(event) =>
                      updateMutation.mutate({ send_before_hour: Number(event.target.value) })
                    }
                  />
                </FormControl>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', pb: 1 }}>
                  Nothing sends outside these hours.
                </Typography>
              </Box>
            </Sheet>
          </DetailSection>

          <DetailSection title="Steps">
            <Box sx={{ display: 'grid', gap: 0.75 }}>
              {steps.map((step) => (
                <Sheet
                  key={step.id}
                  variant="soft"
                  sx={{ p: 1.25, borderRadius: 'sm', display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Chip size="sm" variant="outlined" color="neutral" sx={{ flexShrink: 0 }}>
                    Day {step.day_offset}
                  </Chip>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="body-sm">{step.template_name}</Typography>
                    {step.skip_if_signal && (
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        {SKIP_SIGNALS.find((s) => s.value === step.skip_if_signal)?.label ??
                          `Skip if ${step.skip_if_signal}`}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="danger"
                    aria-label="Remove step"
                    loading={deleteStepMutation.isPending && deleteStepMutation.variables === step.id}
                    onClick={() => deleteStepMutation.mutate(step.id)}
                  >
                    <Trash size={16} />
                  </IconButton>
                </Sheet>
              ))}
              {steps.length === 0 && (
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                  No steps yet — this sequence sends nothing.
                </Typography>
              )}
            </Box>

            {addStepMutation.isError && (
              <Alert color="danger" variant="soft" size="sm">
                {getApiErrorMessage(addStepMutation.error, 'That step could not be added.')}
              </Alert>
            )}

            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', display: 'grid', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <FormControl sx={{ flex: '2 1 200px' }}>
                  <FormLabel>Template</FormLabel>
                  <Select
                    size="sm"
                    placeholder={templates.length ? 'Pick a template' : 'Write a template first'}
                    value={newStep.template || null}
                    onChange={(_, value) =>
                      setNewStep((prev) => ({ ...prev, template: (value as string) ?? '' }))
                    }
                  >
                    {templates.map((template) => (
                      <Option key={template.id} value={template.id}>
                        {template.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ width: { xs: '100%', sm: 110 } }}>
                  <FormLabel>Day</FormLabel>
                  <Input
                    size="sm"
                    type="number"
                    placeholder="3"
                    slotProps={{ input: { min: 0 } }}
                    value={newStep.day_offset}
                    onChange={(event) =>
                      setNewStep((prev) => ({ ...prev, day_offset: event.target.value }))
                    }
                  />
                </FormControl>
              </Box>
              <FormControl>
                <FormLabel>When to skip</FormLabel>
                <Select
                  size="sm"
                  value={newStep.skip}
                  onChange={(_, value) =>
                    setNewStep((prev) => ({ ...prev, skip: (value as string) ?? '' }))
                  }
                >
                  {SKIP_SIGNALS.map((signal) => (
                    <Option key={signal.value || 'always'} value={signal.value}>
                      {signal.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="sm"
                  variant="outlined"
                  startDecorator={<Plus size={16} />}
                  disabled={!newStep.template || newStep.day_offset === ''}
                  loading={addStepMutation.isPending}
                  onClick={() => addStepMutation.mutate()}
                >
                  Add step
                </Button>
              </Box>
            </Sheet>
          </DetailSection>

          <DetailSection title={`Enrolled (${enrollments.length})`}>
            <Box sx={{ display: 'grid', gap: 0.5 }}>
              {enrollments.map((enrollment) => (
                <Box
                  key={enrollment.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}
                >
                  <Chip
                    size="sm"
                    variant="soft"
                    color={ENROLMENT_COLORS[enrollment.status] ?? 'neutral'}
                    sx={{ flexShrink: 0 }}
                  >
                    {enrollment.status}
                  </Chip>
                  <Typography level="body-sm" sx={{ flex: 1, minWidth: 0 }} noWrap>
                    {enrollment.customer_name}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    {enrollment.steps_run} sent
                  </Typography>
                  {enrollment.status === 'active' && (
                    <Button
                      size="sm"
                      variant="plain"
                      color="neutral"
                      loading={cancelEnrollment.isPending && cancelEnrollment.variables === enrollment.id}
                      onClick={() => cancelEnrollment.mutate(enrollment.id)}
                    >
                      Stop
                    </Button>
                  )}
                </Box>
              ))}
              {enrollments.length === 0 && (
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                  Nobody is on this sequence.
                </Typography>
              )}
            </Box>
          </DetailSection>

          <Box>
            <DetailRow label="Created" value={new Date(sequence.created_at).toLocaleDateString()} />
            <DetailRow label="Updated" value={new Date(sequence.updated_at).toLocaleDateString()} />
          </Box>
        </>
      )}
    </DetailDrawer>
  );
};

export default SequenceDetailDrawer;
